<template>
  <div class="audience-form">
    <div class="sections-grid">
      <section
        v-for="section in activeSections"
        :key="section.key"
        class="section-card"
        :class="['mode-' + section.mode, 'state-' + sectionState(section.key)]"
      >
        <header class="section-head">
          <div class="head-left">
            <div class="section-icon"><i class="bi" :class="section.icon" aria-hidden="true"></i></div>
            <div class="head-text">
              <h3 class="section-title">{{ section.label }}</h3>
              <p class="section-desc">{{ section.desc }}</p>
            </div>
          </div>
          <div class="head-right">
            <span class="total-chip" :class="'tone-' + sectionState(section.key)">
              <i class="bi bi-check" v-if="sectionState(section.key) === 'ok'" aria-hidden="true"></i>
              <i class="bi bi-exclamation-triangle-fill" v-else-if="sectionState(section.key) === 'warn'" aria-hidden="true"></i>
              <strong>{{ totals[section.key] || 0 }}%</strong>
            </span>
          </div>
        </header>

        <div class="progress-track">
          <div
            class="progress-fill"
            :class="'tone-' + sectionState(section.key)"
            :style="{ width: Math.min(100, totals[section.key] || 0) + '%' }"
          ></div>
        </div>

        <div class="section-body">
          <div
            v-for="(row, idx) in audiences[section.key]"
            :key="section.key + '-' + idx"
            class="row"
            :class="{ 'row-dynamic': section.mode === 'dynamic' }"
          >
            <div class="row-label" v-if="section.mode !== 'dynamic'">
              <span v-if="section.mode === 'gender'" class="gender-icon" :class="genderClass(row.k)"><i class="bi" :class="genderIcon(row.k)" aria-hidden="true"></i></span>
              {{ formatKey(section.key, row.k) }}
            </div>
            <input
              v-if="section.mode === 'dynamic'"
              type="text"
              v-model="row.k"
              class="text-input"
              :placeholder="section.placeholder"
            >

            <div class="number-wrap">
              <input
                type="number"
                v-model="row.v"
                class="num-input"
                min="0"
                max="100"
                step="0.01"
                placeholder="0"
              >
              <span class="suffix">%</span>
            </div>

            <div class="row-bar">
              <div
                class="row-bar-fill"
                :style="{ width: clampedPercent(row.v) + '%' }"
              ></div>
            </div>

            <button
              v-if="section.mode === 'dynamic'"
              class="delete-btn"
              @click="handleDelete(section.key, idx, row)"
              :title="'Remove ' + (section.singular || 'row')"
            >
              <i class="bi bi-x" aria-hidden="true"></i>
            </button>
          </div>

          <div class="row-actions" v-if="section.mode === 'dynamic'">
            <button class="add-row-btn" @click="addNext(section.key)">
              <i class="bi bi-plus" aria-hidden="true"></i>
              Add {{ section.singular || 'row' }}
            </button>
            <button
              class="add-other-btn"
              @click="addOther(section.key)"
              :disabled="remainder(section.key) <= 0"
              :title="otherButtonTitle(section.key)"
            >
              <i class="bi bi-plus-circle" aria-hidden="true"></i>
              <span>Add <strong>other</strong></span>
              <span class="remainder-chip" v-if="remainder(section.key) > 0">+{{ remainder(section.key) }}%</span>
              <span class="remainder-chip remainder-chip-ok" v-else>100%</span>
            </button>
          </div>

          <p class="empty-hint" v-if="section.mode === 'dynamic' && !audiences[section.key].length">
            No {{ section.singular }} entries yet. Click <strong>Add {{ section.singular }}</strong> above or collect from API.
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
const ICONS = {
  star: 'bi-star-fill',
  people: 'bi-people-fill',
  calendar: 'bi-calendar',
  gender: 'bi-gender-ambiguous',
  globe: 'bi-globe2',
  building: 'bi-buildings',
  female: 'bi-gender-female',
  male: 'bi-gender-male'
};

const KEY_LABELS = {
  quality_distribution: {
    'verygood': 'Very good',
    'good': 'Good',
    'medium': 'Medium',
    'bad': 'Bad',
    'verybad': 'Very bad'
  },
  followers: {
    'ghost_followers': 'Ghost followers',
    'mass_followers': 'Mass followers',
    'influencer': 'Influencer',
    'real_people': 'Real people'
  },
  age: {
    '13.0-17.0': '13–17',
    '18.0-24.0': '18–24',
    '25.0-34.0': '25–34',
    '35.0-44.0': '35–44',
    '45.0-54.0': '45–54',
    '55.0-64.0': '55–64',
    '65.0-*': '65+'
  },
  gender: {
    1: 'Female',
    2: 'Male'
  }
};

export default {
  name: "audience-form",
  props: {
    audiences: { type: Object, required: true },
    loader: { type: Object, required: true },
    module: { type: Array, required: true }
  },
  data() {
    return {
      sectionConfig: [
        { key: 'quality_distribution', label: 'Quality distribution', desc: 'Breakdown by audience quality.', mode: 'fixed', icon: ICONS.star },
        { key: 'followers', label: 'Followers', desc: 'Type of accounts following.', mode: 'fixed', icon: ICONS.people },
        { key: 'age', label: 'Age', desc: 'Audience age distribution.', mode: 'fixed', icon: ICONS.calendar },
        { key: 'gender', label: 'Gender', desc: 'Female / male split.', mode: 'gender', icon: ICONS.gender },
        { key: 'lang', label: 'Language', desc: 'Languages spoken by the audience.', mode: 'dynamic', placeholder: 'e.g. en, pl, cs', singular: 'language', icon: ICONS.globe },
        { key: 'city', label: 'City', desc: 'Top cities of the audience.', mode: 'dynamic', placeholder: 'City name', singular: 'city', icon: ICONS.building }
      ]
    };
  },
  computed: {
    activeSections() {
      return this.sectionConfig.filter(s => this.module.includes(s.key));
    },
    totals() {
      const result = {};
      for (const key of Object.keys(this.audiences)) {
        let sum = 0;
        const rows = this.audiences[key] || [];
        for (const row of rows) {
          if (row.v === '' || row.v === null || row.v === undefined) continue;
          const n = parseFloat(row.v);
          if (!isNaN(n)) sum += n;
        }
        result[key] = Math.round(sum);
      }
      return result;
    }
  },
  methods: {
    sectionState(key) {
      const total = this.totals[key] || 0;
      if (total === 100) return 'ok';
      if (total === 0) return 'empty';
      return 'warn';
    },
    clampedPercent(v) {
      const n = parseFloat(v);
      if (isNaN(n)) return 0;
      return Math.min(100, Math.max(0, n));
    },
    formatKey(section, k) {
      const map = KEY_LABELS[section];
      if (map && map[k] !== undefined) return map[k];
      return k;
    },
    genderClass(k) {
      return k === 1 ? 'gender-female' : 'gender-male';
    },
    genderIcon(k) {
      return k === 1 ? ICONS.female : ICONS.male;
    },
    handleDelete(type, idx, row) {
      if (row.id > 0) {
        this.deleteAudienceDetail(row.id, idx, type);
      } else {
        this.deleteRow(type, idx);
      }
    },
    deleteAudienceDetail(id, idx, type) {
      this.$emit('deleteAudienceDetail', id, idx, type);
    },
    addNext(type) {
      this.audiences[type].push({ id: 0, k: '', v: '' });
    },
    remainder(type) {
      return Math.max(0, 100 - (this.totals[type] || 0));
    },
    otherButtonTitle(type) {
      const gap = this.remainder(type);
      if (gap > 0) {
        return 'Fill the missing ' + gap + "% as 'other'";
      }
      return 'Section already totals 100%';
    },
    addOther(type) {
      const gap = this.remainder(type);
      if (gap <= 0) return;
      this.audiences[type].push({ id: 0, k: 'other', v: gap });
    },
    deleteRow(type, idx) {
      this.audiences[type].splice(idx, 1);
    },
    countSummaryPercent(type) {
      return this.totals[type] || 0;
    }
  }
}
</script>

<style scoped>
  .audience-form {
    padding: 18px 4px 4px;
  }
  .sections-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(440px, 1fr));
    gap: 16px;
  }

  .section-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    overflow: hidden;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .section-card.state-ok {
    border-color: #a7f3d0;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.06);
  }
  .section-card.state-warn {
    border-color: #fde68a;
  }

  .section-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 16px 18px 12px;
  }
  .head-left {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    min-width: 0;
    flex: 1;
  }
  .section-icon {
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #eaf1f9;
    color: #265a88;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .section-icon .bi { font-size: 18px; }
  .section-card.state-ok .section-icon { background: #d1fae5; color: #065f46; }
  .section-card.state-warn .section-icon { background: #fef3c7; color: #92400e; }

  .head-text { min-width: 0; }
  .section-title {
    font-size: 14.5px;
    font-weight: 600;
    margin: 0 0 2px;
    color: #0f172a;
  }
  .section-desc {
    font-size: 12px;
    color: #6b7280;
    margin: 0;
    line-height: 1.4;
  }

  .total-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    padding: 3px 10px 3px 8px;
    font-size: 12px;
    font-weight: 500;
    flex-shrink: 0;
  }
  .total-chip strong { color: #0f172a; font-weight: 700; }
  .total-chip.tone-ok {
    background: #d1fae5;
    color: #065f46;
    border-color: #a7f3d0;
  }
  .total-chip.tone-ok strong { color: #065f46; }
  .total-chip.tone-warn {
    background: #fef3c7;
    color: #92400e;
    border-color: #fde68a;
  }
  .total-chip.tone-warn strong { color: #92400e; }
  .total-chip.tone-empty { opacity: 0.7; }

  .progress-track {
    height: 3px;
    background: #f1f5f9;
    overflow: hidden;
  }
  .progress-fill {
    height: 100%;
    background: #cbd5e1;
    transition: width 0.25s ease, background 0.2s;
  }
  .progress-fill.tone-ok { background: linear-gradient(90deg, #10b981, #34d399); }
  .progress-fill.tone-warn { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
  .progress-fill.tone-empty { background: #e2e8f0; }

  .section-body {
    padding: 14px 18px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .row {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) 110px minmax(60px, 1fr);
    gap: 12px;
    align-items: center;
  }
  .row-dynamic {
    grid-template-columns: minmax(0, 1fr) 110px minmax(60px, 100px) 32px;
  }

  .row-label {
    font-size: 13px;
    color: #0f172a;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .gender-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    flex-shrink: 0;
  }
  .gender-female { background: #fce7f3; color: #be185d; }
  .gender-male { background: #dbeafe; color: #1d4ed8; }

  .text-input {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 7px 10px;
    font-size: 13px;
    background: #fff;
    color: #0f172a;
    transition: border-color 0.15s, box-shadow 0.15s;
    min-width: 0;
  }
  .text-input:focus {
    outline: none;
    border-color: #265a88;
    box-shadow: 0 0 0 3px rgba(38, 90, 136, 0.12);
  }

  .number-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }
  .num-input {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 7px 28px 7px 10px;
    font-size: 13px;
    background: #fff;
    color: #0f172a;
    text-align: right;
    transition: border-color 0.15s, box-shadow 0.15s;
    -moz-appearance: textfield;
  }
  .num-input::-webkit-outer-spin-button,
  .num-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .num-input:focus {
    outline: none;
    border-color: #265a88;
    box-shadow: 0 0 0 3px rgba(38, 90, 136, 0.12);
  }
  .suffix {
    position: absolute;
    right: 10px;
    color: #6b7280;
    font-size: 12px;
    font-weight: 500;
    pointer-events: none;
  }

  .row-bar {
    height: 6px;
    background: #f1f5f9;
    border-radius: 999px;
    overflow: hidden;
    min-width: 40px;
  }
  .row-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #93c5fd, #265a88);
    border-radius: 999px;
    transition: width 0.2s ease;
  }
  .section-card.state-ok .row-bar-fill {
    background: linear-gradient(90deg, #6ee7b7, #10b981);
  }

  .delete-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: transparent;
    border: 1px solid transparent;
    color: #6b7280;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
    flex-shrink: 0;
  }
  .delete-btn:hover {
    background: #fef2f2;
    color: #dc2626;
    border-color: #fecaca;
  }

  .row-actions {
    margin-top: 4px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .add-row-btn,
  .add-other-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 9px 12px;
    background: #fafbfc;
    border: 1px dashed #d1d5db;
    color: #475569;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
  }
  .add-row-btn:hover,
  .add-other-btn:hover:not(:disabled) {
    border-color: #265a88;
    color: #265a88;
    background: #fff;
    border-style: solid;
  }
  .add-other-btn {
    background: #fffbeb;
    border-color: #fde68a;
    color: #92400e;
  }
  .add-other-btn:hover:not(:disabled) {
    background: #fef3c7;
    border-color: #f59e0b;
    color: #92400e;
  }
  .add-other-btn:disabled {
    cursor: not-allowed;
    background: #f8fafc;
    border-color: #e5e7eb;
    color: #9ca3af;
  }
  .add-other-btn strong { font-weight: 600; }
  .remainder-chip {
    background: rgba(245, 158, 11, 0.15);
    color: #92400e;
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
  }
  .remainder-chip-ok {
    background: rgba(16, 185, 129, 0.15);
    color: #065f46;
  }
  .add-other-btn:disabled .remainder-chip {
    background: rgba(148, 163, 184, 0.15);
    color: #64748b;
  }

  .empty-hint {
    margin: 4px 0 0;
    padding: 12px;
    background: #fafbfc;
    border-radius: 8px;
    color: #6b7280;
    font-size: 12.5px;
    text-align: center;
  }
  .empty-hint strong { color: #265a88; font-weight: 600; }

  @media (max-width: 540px) {
    .sections-grid {
      grid-template-columns: 1fr;
    }
    .row {
      grid-template-columns: minmax(0, 1fr) 90px;
    }
    .row .row-bar { display: none; }
    .row-dynamic {
      grid-template-columns: minmax(0, 1fr) 90px 32px;
    }
    .row-actions {
      grid-template-columns: 1fr;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/audience/AudienceForm.vue